if (!customElements.get('pdp-upsell-products')) {
  customElements.define(
    'pdp-upsell-products',
    class PdpUpsellProducts extends HTMLElement {
      connectedCallback() {
        this.locationPrefix = this.dataset.locationPrefix || '';
        this.cards = Array.from(this.querySelectorAll('.pdp-upsell-products__card'));

        this.quickView = document.querySelector('.quickview-wrap');
        if (this.quickView) {
          this.quickViewPopup = this.quickView.querySelector('.quickview-popup');
          this.quickViewClose = this.quickView.querySelector('.qv-close');
          this.quickViewContent = this.quickView.querySelector('.qv-product-content');
          this.sliderImages = this.quickView.querySelector('.qv-slider-wrap .slider-images');
          this.sliderThumbnails = this.quickView.querySelector('.qv-slider-wrap .slider-thumbnails');
          this.prevArrow = this.quickView.querySelector('.qv-slider-wrap .prev-arrow');
          this.nextArrow = this.quickView.querySelector('.qv-slider-wrap .next-arrow');
        }

        this.handleToggleClick = this.handleToggleClick.bind(this);
        this.handleQuickViewClick = this.handleQuickViewClick.bind(this);
        this.handleOutsideClick = this.handleOutsideClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleClose = this.handleClose.bind(this);

        this.cards.forEach((card) => {
          card.querySelector('[data-upsell-toggle]')?.addEventListener('click', this.handleToggleClick);
          card.querySelectorAll('[data-upsell-quick-view]').forEach((trigger) => {
            trigger.addEventListener('click', this.handleQuickViewClick);
          });
        });

        this.quickViewClose?.addEventListener('click', this.handleClose);
        document.addEventListener('click', this.handleOutsideClick);
        document.addEventListener('keydown', this.handleKeydown);
      }

      disconnectedCallback() {
        this.quickViewClose?.removeEventListener('click', this.handleClose);
        document.removeEventListener('click', this.handleOutsideClick);
        document.removeEventListener('keydown', this.handleKeydown);
        this.prevArrow?.removeEventListener('click', this.handlePrevClick);
        this.nextArrow?.removeEventListener('click', this.handleNextClick);
      }

      handleToggleClick(event) {
        const toggle = event.currentTarget;
        const card = toggle.closest('.pdp-upsell-products__card');
        const variantId = toggle.dataset.variantId;

        if (toggle.classList.contains('is-added')) {
          toggle.classList.remove('is-added');
          this.toggleCartItem(variantId, 0);
          return;
        }

        card?.querySelector('.button-atc')?.click();
        toggle.classList.add('is-added');
      }

      toggleCartItem(variantId, quantity) {
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        fetch(routes.cart_change_url, {
          ...fetchConfig(),
          body: JSON.stringify({
            id: variantId,
            quantity,
            sections: cart ? cart.getSectionsToRender().map((section) => section.section) : undefined,
            sections_url: window.location.pathname,
          }),
        })
          .then((response) => response.json())
          .then((state) => {
            if (cart) cart.renderContents(state);
          })
          .catch((error) => console.error(error));
      }

      handleQuickViewClick(event) {
        const trigger = event.currentTarget;
        const card = trigger.closest('.pdp-upsell-products__card');
        if (!card || !this.quickView) return;

        const handle = trigger.dataset.productHandle;
        const priceText = card.querySelector('.pdp-upsell-products__regular-price')?.textContent.trim() || '';
        const template = card.querySelector('[data-upsell-popup-form]');

        this.lastFocusedElement = trigger;

        fetch(`/products/${handle}.json`)
          .then((response) => response.json())
          .then(({ product }) => {
            this.renderQuickView(product, priceText, template);
            this.openPopup();
          })
          .catch((error) => console.error(error));
      }

      renderQuickView(product, priceText, template) {
        this.clearQuickView();

        const visibleImages = (product.images || []).filter((image) => image.alt !== 'hide');
        visibleImages.forEach((image) => {
          const fullImage = document.createElement('img');
          fullImage.src = image.src;
          fullImage.alt = product.title;
          this.sliderImages.appendChild(fullImage);
          this.sliderThumbnails.appendChild(fullImage.cloneNode(true));
        });

        const hasMultipleImages = visibleImages.length > 1;
        this.prevArrow?.classList.toggle('hidden', !hasMultipleImages);
        this.nextArrow?.classList.toggle('hidden', !hasMultipleImages);
        this.sliderThumbnails?.classList.toggle('hidden', !hasMultipleImages);

        const titleEl = document.createElement('h3');
        titleEl.className = 'product-title';
        titleEl.textContent = product.title;

        const priceEl = document.createElement('div');
        priceEl.className = 'product-price';
        priceEl.textContent = priceText;

        const formEl = document.createElement('div');
        formEl.className = 'product-form';
        if (template) formEl.appendChild(template.content.cloneNode(true));

        const variantSelect = this.buildVariantSelect(product);
        if (variantSelect) {
          formEl.appendChild(variantSelect);
          this.bindVariantSelect(variantSelect, formEl);
        }

        const descriptionEl = document.createElement('div');
        descriptionEl.className = 'product-description';
        descriptionEl.innerHTML = product.body_html || '';

        const detailsEl = document.createElement('div');
        detailsEl.className = 'qv-modal-full-details-wrapper';
        const detailsLink = document.createElement('a');
        detailsLink.className = 'qv-modal-full-details';
        detailsLink.href = `${this.locationPrefix}/products/${product.handle}`;
        detailsLink.textContent = 'View full product details';
        detailsEl.appendChild(detailsLink);

        this.quickViewContent.append(titleEl, priceEl, formEl, descriptionEl, detailsEl);

        if (hasMultipleImages) this.bindSlider();
      }

      buildVariantSelect(product) {
        if (!product.variants || product.variants.length < 2) return null;

        const select = document.createElement('select');
        select.className = 'qv-form-select';
        select.setAttribute('aria-label', 'Variant');

        product.variants.forEach((variant) => {
          const option = document.createElement('option');
          option.value = variant.id;
          option.textContent = variant.title;
          select.appendChild(option);
        });

        return select;
      }

      bindVariantSelect(select, formEl) {
        select.addEventListener('change', () => {
          const input = formEl.querySelector('input.product-variant-id');
          if (input) input.value = select.value;
        });
      }

      bindSlider() {
        this.prevArrow?.removeEventListener('click', this.handlePrevClick);
        this.nextArrow?.removeEventListener('click', this.handleNextClick);

        const images = Array.from(this.sliderImages.querySelectorAll('img'));
        const thumbnails = Array.from(this.sliderThumbnails.querySelectorAll('img'));
        let currentIndex = 0;

        const showImage = (index) => {
          currentIndex = index;
          images.forEach((image, i) => image.classList.toggle('is-active', i === currentIndex));
          thumbnails.forEach((thumbnail, i) => thumbnail.classList.toggle('active', i === currentIndex));
        };

        showImage(0);

        this.handlePrevClick = () => showImage(currentIndex === 0 ? images.length - 1 : currentIndex - 1);
        this.handleNextClick = () => showImage(currentIndex === images.length - 1 ? 0 : currentIndex + 1);

        this.prevArrow?.addEventListener('click', this.handlePrevClick);
        this.nextArrow?.addEventListener('click', this.handleNextClick);

        thumbnails.forEach((thumbnail, index) => {
          thumbnail.addEventListener('click', () => showImage(index));
        });
      }

      openPopup() {
        this.quickView.classList.add('is-open');
        this.quickView.setAttribute('role', 'dialog');
        this.quickView.setAttribute('aria-modal', 'true');
        this.quickViewClose?.focus();
      }

      handleClose() {
        if (!this.quickView) return;
        this.quickView.classList.remove('is-open');
        this.quickView.removeAttribute('role');
        this.quickView.removeAttribute('aria-modal');
        this.clearQuickView();
        this.lastFocusedElement?.focus();
      }

      clearQuickView() {
        if (this.quickViewContent) this.quickViewContent.innerHTML = '';
        if (this.sliderImages) this.sliderImages.innerHTML = '';
        if (this.sliderThumbnails) this.sliderThumbnails.innerHTML = '';
      }

      handleOutsideClick(event) {
        if (!this.quickView?.classList.contains('is-open')) return;
        if (this.quickViewPopup && !this.quickViewPopup.contains(event.target)) {
          this.handleClose();
        }
      }

      handleKeydown(event) {
        if (event.key === 'Escape' && this.quickView?.classList.contains('is-open')) {
          this.handleClose();
        }
      }
    }
  );
}
